import React, { useCallback } from "react";
import { UserGroupsModel } from "../../../../../utils/apis/user_groups_repository";
import { Autocomplete, TextField } from "@mui/material";
import { SelectDropDown } from "../../../../ui/inputs/SelectDropDown";
import { TenantsData, getTenants } from "../../../../../utils/apis/tenants_repository";
import { UserModel, getAllUsers } from "../../../../../utils/apis/user_repository";
import { RulesData } from "../../../../../utils/apis/rules_repository";
import { getUserFromLocalStorage } from "../../../../../utils/helper";

interface UserGroupsBodyProps {
    userGroup?: UserGroupsModel;
    setUserGroup: React.Dispatch<React.SetStateAction<UserGroupsModel>>;
    setShowSubmitButton: React.Dispatch<React.SetStateAction<boolean>>;
}

export const UserGroupsBody: React.FC<UserGroupsBodyProps> = ({ userGroup, setUserGroup, setShowSubmitButton }) => {

    const [tenants, setTenants] = React.useState<TenantsData[]>([]);
    const [tenantSelected, setTenantSelected] = React.useState<string>('');
    const [users, setUsers] = React.useState<UserModel[]>([]);
    const [appliedUsers, setAppliedUsers] = React.useState<UserModel[]>([]);
    const [rules, setRules] = React.useState<RulesData[]>([]);
    const [appliedRules, setAppliedRules] = React.useState<RulesData[]>([]);
    const [userInputWidth, setUserInputWidth] = React.useState<number>(250);
    const [ruleInputWidth, setRuleInputWidth] = React.useState<number>(250);
    const [userGroupName, setUserGroupName] = React.useState<string>('');
    const [userGroupDescription, setUserGroupDescription] = React.useState<string>('');

    // Read once: a fresh object each render re-ran the loading below on every render.
    const [user] = React.useState(getUserFromLocalStorage);

    // The group as it was when the sheet opened. Every edit is handed back through setUserGroup and returns here as
    // `userGroup`, so loading the fields from that reset them mid-edit and, before the people had loaded, replaced the
    // group's people and rules with none. The fields load once per group, from this.
    const [openedGroup, setOpenedGroup] = React.useState(userGroup);
    if (userGroup?.groupId !== openedGroup?.groupId) setOpenedGroup(userGroup);
    // Nothing is handed back until the group's people and rules are in the fields.
    const [loaded, setLoaded] = React.useState(false);

    const initializeData = useCallback(() => {
        setLoaded(false);
        if (openedGroup) {
            setUserGroupName(openedGroup.name);
            setUserGroupDescription(openedGroup.description);
            setTenantSelected(openedGroup.tenant);
        } else {
            setTenantSelected(user.tenant);
        }

        if (user.role === 'SuperAdmin') {
            getTenants().then((data) => {
                setTenants(data);
            });
        } else {
            setTenants([
                {
                    name: user.tenant,
                    futureBookingPeriod: 0,
                    bookingLimit: 0,
                    lastMinuteBookingPeriod: 1,
                    fulldayDefaultTimes: {
                        startHour: 800,
                        endHour: 1800

                    },
                    gracePeriod: 0,
                    earlyArrival: 0,
                    isWeekend: false,
                    isMulti: false,
                    tenantId: user.tenant,
                    tenantRules: [],
                    liveBookingOccupancy: 0,
                    liveGuestOccupancy: 0,
                    liveVIPOccupancy: 0,
                    availableOccupancy: 0,
                    totalAllocation: 0,
                }
            ]);
        }

        const userSpecificRules: RulesData[] = [
            {
                title: "Booking Limit",
                summary: "Bookings per week.",
                description: "Restrict users to book only a specific number of times per week.",
                status: true,
                statuses: {},
                value: "1",
                tenants: [],
                users: [],
                userGroups: [],
                mobileOption: {}
            },
            {
                title: "Future Bookings",
                summary: "Bookings in advance.",
                description: "How far in the future a booking can be made.",
                status: true,
                statuses: {},
                value: "0",
                tenants: [],
                users: [],
                userGroups: [],
                mobileOption: {}
            },
            {
                title: "Weekend",
                summary: "Allow weekend bookings.",
                description: "Allow weekend bookings",
                status: true,
                statuses: {},
                value: "false",
                tenants: [],
                users: [],
                userGroups: [],
                mobileOption: {}
            },
            {
                title: "Multiple Days",
                summary: "Allow multiple day bookings.",
                description: "Allow multiple day bookings",
                status: true,
                statuses: {},
                value: "false",
                tenants: [],
                users: [],
                userGroups: [],
                mobileOption: {}
            }
        ];
        setRules(userSpecificRules);

        // Initial users fetch
        getAllUsers(openedGroup?.tenant || user.tenant).then((data) => {
            const filteredUsers = data.filter((u) => u.role !== 'Pending');
            setUsers(filteredUsers);
            if (openedGroup) {
                // A member the list doesn't offer (pending, say) is kept as the group has them, so saving doesn't drop them.
                setAppliedUsers(openedGroup.users.map((member) =>
                    filteredUsers.find((u) => u.userId === member.userId) ?? (member as UserModel)));
                setAppliedRules((openedGroup.appliedRules ?? []).map((title) =>
                    userSpecificRules.find((r) => r.title === title)).filter((rule): rule is RulesData => Boolean(rule)));
            }
            setLoaded(true);
        });
    }, [openedGroup, user]);

    React.useEffect(() => {
        initializeData();
    }, [initializeData]);

    // Only SuperAdmin can change tenant, so only fetch users on tenant change if SuperAdmin
    React.useEffect(() => {
        if (user.role === 'SuperAdmin' && tenantSelected) {
            getAllUsers(tenantSelected).then((data) => {
                const filteredUsers = data.filter((u) => u.role !== 'Pending');
                setUsers(filteredUsers);
            });
        }
    }, [tenantSelected, user.role]);

    React.useEffect(() => {
        if (appliedUsers.length > 3) {
            if (appliedUsers.length === 3) {
                setUserInputWidth(300);
            } else if (appliedUsers.length === 4) {
                setUserInputWidth(350);
            } else if (appliedUsers.length === 5) {
                setUserInputWidth(400);
            } else if (appliedUsers.length === 6) {
                setUserInputWidth(450);
            } else {
                setUserInputWidth(500);
            }
        } else {
            setUserInputWidth(250);
        }
    }, [appliedUsers]); // as the users are being added, increase the width of the users input field

    React.useEffect(() => {
        if (appliedRules.length > 3) {
            if (appliedRules.length === 3) {
                setRuleInputWidth(300);
            } else if (appliedRules.length === 4) {
                setRuleInputWidth(350);
            } else if (appliedRules.length === 5) {
                setRuleInputWidth(400);
            } else if (appliedRules.length === 6) {
                setRuleInputWidth(450);
            } else {
                setRuleInputWidth(500);
            }
        } else {
            setRuleInputWidth(250);
        }
    }, [appliedRules]); // as the rules are being added, increase the width of the rules input field

    React.useEffect(() => {
        if (!loaded) return;
        if (userGroupName !== '' && !openedGroup) {
            setUserGroup({
                name: userGroupName,
                description: userGroupDescription,
                tenant: tenantSelected,
                users: appliedUsers,
                appliedRules: appliedRules.map((rule) => rule.title)
            });
            // setShowSubmitButton(true);
        } else if (openedGroup) {
            const sameUsers = appliedUsers.map((u) => u.userId).sort().join() === openedGroup.users.map((u) => u.userId).sort().join();
            const sameRules = appliedRules.map((r) => r.title).sort().join() === [...(openedGroup.appliedRules ?? [])].sort().join();
            if (userGroupName !== openedGroup.name || userGroupDescription !== openedGroup.description || tenantSelected !== openedGroup.tenant || !sameUsers || !sameRules) {
                // console.log("Got user group. values arent equal")
                setUserGroup({
                    groupId: openedGroup.groupId,
                    name: userGroupName,
                    description: userGroupDescription,
                    tenant: tenantSelected,
                    users: appliedUsers,
                    appliedRules: appliedRules.map((rule) => rule.title)
                });
                setShowSubmitButton(true);
            } else {
                // console.log("Got user group. values are equal")
                setShowSubmitButton(false);
            }
        } else {
            console.log("None")
        }
    }, [loaded, userGroupName, userGroupDescription, tenantSelected, appliedUsers, appliedRules, openedGroup, setUserGroup, setShowSubmitButton]); // if all the fields are filled or changes were made, gather and set the final userGroup and show the submit button

    return (
        <>
            <div className="flex flex-col items-between mt-12 mx-6">

                <span className='text-md font-medium text-secondaryText my-4'>Group Name</span>
                <div className="custom-select">
                    <TextField className="bg-white w-64" onChange={(e) => {
                        setUserGroupName(e.target.value);
                        // if the value is not empty, show the submit button
                        if (e.target.value !== '') {
                            setShowSubmitButton(true);
                        } else {
                            setShowSubmitButton(false);
                        }
                    }} error={userGroupName === ''} helperText={userGroupName === ''? "Required" : null} value={userGroupName}></TextField>
                </div>

                <span className='text-md font-medium text-secondaryText my-4'>Description</span>
                <div className="custom-select">
                    <TextField className="bg-white w-64" onChange={(e) => {
                        setUserGroupDescription(e.target.value);
                        setShowSubmitButton(true)
                    }} value={userGroupDescription}></TextField>
                </div>

                {/* display only if the tenants list is more than 1 items long, because, for tenant admins, it'll always be their tenant */}
                <div className="custom-select" hidden={tenants.length <= 1}>
                <span className='text-md font-medium text-secondaryText my-4'>Select Tenant</span>
                    <SelectDropDown
                        className="bg-white"
                        onChange={(e) => {
                            setTenantSelected(e.target.value);
                            setAppliedUsers([]); // Clear selected users when tenant changes
                            setShowSubmitButton(true);
                        }}
                        value={tenantSelected}
                    >
                        <option value={''} key={''}>Select Tenant</option>
                        {tenants.map((tenant) => (
                            <option value={tenant.name} key={tenant.name}>{tenant.name}</option>
                        ))}
                    </SelectDropDown>
                </div>

                <span className='text-md font-medium text-secondaryText my-4' hidden={userGroupName === ''}>Select Users</span>
                <div hidden={userGroupName === ''}>
                    <Autocomplete
                        disablePortal
                        id="combo-box"
                        options={users.map((user) => user.name)}
                        multiple
                        value={appliedUsers.map((user) => user.name)}
                        limitTags={4}
                        onChange={(event, value) => {
                            // A member kept from the group, though the list doesn't offer them, is found among the members.
                            setAppliedUsers(value.map((name) => {
                                return users.find((user) => user.name === name) ?? appliedUsers.find((user) => user.name === name) as UserModel;
                            }));
                            setShowSubmitButton(true)
                        }}
                        sx={{ width: userInputWidth }}
                        renderInput={(params) => <TextField {...params} label="Select Users" />}
                    />
                </div>

                <span className='text-md font-medium text-secondaryText my-4' hidden={userGroupName === ''}>Apply Rules</span>
                <div hidden={userGroupName === ''}>
                    <Autocomplete
                        disablePortal
                        id="combo"
                        options={rules.map((rule) => rule.title)}
                        multiple
                        value={appliedRules.map((rule) => rule.title)}
                        limitTags={4}
                        onChange={(event, value) => {
                            console.log(value);
                            setAppliedRules(value.map((title) => {
                                return rules.filter((rule) => rule.title === title)[0];
                            }));
                            setShowSubmitButton(true)
                        }}
                        sx={{ width: ruleInputWidth }}
                        renderInput={(params) => <TextField {...params} label="Applied Rules" />}
                    />
                </div>
            </div>
        </>
    );
};

export default UserGroupsBody;
